import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiCall } from '../hooks/useApi';
import useAuthStore from './authStore';

// Subscription tier definitions - must match backend
export const SUBSCRIPTION_TIERS = {
  STARTER: 'starter',
  GROWTH: 'growth',
  PRO: 'pro',
  ENTERPRISE: 'enterprise',
};

// Legacy tier mapping for backward compatibility
const LEGACY_TIER_MAP = {
  free: 'starter',
  full: 'pro',
};

const normalizeTier = tier => {
  if (!tier) return SUBSCRIPTION_TIERS.STARTER;
  return LEGACY_TIER_MAP[tier] || tier;
};

// Mirrors ACTIVE_ACCESS_STATUSES in server/src/billing/entitlements.py. GET /entitlements reports
// the server's own access decision: past_due / grace_period while grace is live (a lapsed grace is
// rewritten to 'expired' server-side), which still counts as active access. Keep in lockstep.
const ENTITLED_STATUSES = new Set(['active', 'trialing', 'none', 'grace_period', 'past_due']);

// Feature definitions with tier requirements
export const FEATURES = {
  // Dashboard & Analytics
  ETSY_DASHBOARD: 'etsy_dashboard',
  BASIC_ANALYTICS: 'analytics_basic',

  // Mockup Features
  MOCKUP_GENERATOR: 'batch_mockups',
  UNLIMITED_MOCKUPS: 'batch_mockups',
  BATCH_MOCKUPS: 'batch_mockups',

  // File Management
  FILE_CLEANER: 'file_cleaner',
  FILE_RESIZING: 'advanced_resizing',
  BULK_FILE_OPERATIONS: 'batch_uploads',

  // Listing Management
  LISTING_TEMPLATES: 'batch_uploads',
  AUTO_NAMING: 'auto_naming',
  BATCH_UPLOADS: 'batch_uploads',

  // Print Shop Features
  PRINT_FILE_GENERATOR: 'print_file_generation',
  ADVANCED_RESIZING: 'advanced_resizing',
  CSV_EXPORT: 'csv_export',
  MULTI_SHOP_SUPPORT: 'multi_shop_support',

  // Platform integrations
  ETSY_INTEGRATION: 'etsy_integration',
  SHOPIFY_INTEGRATION: 'shopify_integration',
  CRAFTFLOW_COMMERCE: 'craftflow_commerce',

  // AI & Automation
  AI_TAGGING: 'ai_tagging',
  AUTO_LAYOUT: 'auto_layout',
  ADVANCED_LAYOUT: 'advanced_layout',
  DPI_SCORING: 'dpi_scoring',
  UTILIZATION_METER: 'utilization_meter',

  // Analytics
  ANALYTICS_BASIC: 'analytics_basic',
  ANALYTICS_ADVANCED: 'analytics_advanced',

  // Automation
  AUTOMATION_BASIC: 'automation_basic',
  AUTOMATION_ADVANCED: 'automation_advanced',

  // Enterprise features
  WHITE_LABEL: 'white_label',
  PRIORITY_QUEUE: 'priority_queue',
  SCHEDULED_RUNS: 'scheduled_runs',
  MULTI_USER: 'multi_user',
  ROLE_BASED_ACCESS: 'role_based_access',
  PRIORITY_SUPPORT: 'priority_support',
  CUSTOM_DOMAIN: 'custom_domain',
  CMS_FULL: 'cms_full',
  ORDER_BULK_ACTIONS: 'order_bulk_actions',
  TRANSACTIONAL_EMAIL: 'transactional_email',
  STRIPE_CHECKOUT: 'stripe_checkout',

  // Limits
  MONTHLY_MOCKUP_LIMIT: 'monthly_mockup_limit',
};

// Tier configurations - matches backend tiers
export const TIER_CONFIGS = {
  [SUBSCRIPTION_TIERS.STARTER]: {
    name: 'Starter',
    price: 29,
    description: 'Everything you need to get started selling',
    features: {
      [FEATURES.ETSY_INTEGRATION]: true,
      [FEATURES.BATCH_MOCKUPS]: true,
      [FEATURES.FILE_CLEANER]: true,
      [FEATURES.AUTO_NAMING]: true,
      [FEATURES.BATCH_UPLOADS]: true,
      [FEATURES.ADVANCED_RESIZING]: true,
      [FEATURES.PRINT_FILE_GENERATOR]: true,
      [FEATURES.CSV_EXPORT]: true,
      [FEATURES.DPI_SCORING]: true,
      [FEATURES.ANALYTICS_BASIC]: true,
      [FEATURES.AUTOMATION_BASIC]: true,
    },
    limits: {
      mockupsPerMonth: 50,
      mockupsPerBatch: -1,
      templates: -1,
      canvas: -1,
      sizes: -1,
      etsyStores: 1,
      shopifyStores: 0,
      exportsPerMonth: 50,
      userSeats: 1,
    },
    color: 'blue',
  },
  [SUBSCRIPTION_TIERS.GROWTH]: {
    name: 'Growth',
    price: 99,
    description: 'Scale your business with powerful tools',
    features: {
      [FEATURES.ETSY_INTEGRATION]: true,
      [FEATURES.SHOPIFY_INTEGRATION]: true,
      [FEATURES.BATCH_MOCKUPS]: true,
      [FEATURES.FILE_CLEANER]: true,
      [FEATURES.AUTO_NAMING]: true,
      [FEATURES.BATCH_UPLOADS]: true,
      [FEATURES.ADVANCED_RESIZING]: true,
      [FEATURES.PRINT_FILE_GENERATOR]: true,
      [FEATURES.CSV_EXPORT]: true,
      [FEATURES.AI_TAGGING]: true,
      [FEATURES.AUTO_LAYOUT]: true,
      [FEATURES.DPI_SCORING]: true,
      [FEATURES.UTILIZATION_METER]: true,
      [FEATURES.CUSTOM_DOMAIN]: true,
      [FEATURES.CMS_FULL]: true,
      [FEATURES.ORDER_BULK_ACTIONS]: true,
      [FEATURES.ANALYTICS_BASIC]: true,
      [FEATURES.AUTOMATION_BASIC]: true,
      [FEATURES.CRAFTFLOW_COMMERCE]: true,
      [FEATURES.TRANSACTIONAL_EMAIL]: true,
      [FEATURES.STRIPE_CHECKOUT]: true,
    },
    limits: {
      mockupsPerMonth: 300,
      mockupsPerBatch: -1,
      templates: -1,
      canvas: -1,
      sizes: -1,
      etsyStores: 1,
      shopifyStores: 1,
      exportsPerMonth: 300,
      userSeats: 1,
    },
    color: 'purple',
  },
  [SUBSCRIPTION_TIERS.PRO]: {
    name: 'Pro',
    price: 249,
    description: 'Advanced automation for high-volume sellers',
    features: {
      [FEATURES.ETSY_INTEGRATION]: true,
      [FEATURES.SHOPIFY_INTEGRATION]: true,
      [FEATURES.BATCH_MOCKUPS]: true,
      [FEATURES.FILE_CLEANER]: true,
      [FEATURES.AUTO_NAMING]: true,
      [FEATURES.BATCH_UPLOADS]: true,
      [FEATURES.ADVANCED_RESIZING]: true,
      [FEATURES.PRINT_FILE_GENERATOR]: true,
      [FEATURES.CSV_EXPORT]: true,
      [FEATURES.AI_TAGGING]: true,
      [FEATURES.AUTO_LAYOUT]: true,
      [FEATURES.ADVANCED_LAYOUT]: true,
      [FEATURES.DPI_SCORING]: true,
      [FEATURES.UTILIZATION_METER]: true,
      [FEATURES.CUSTOM_DOMAIN]: true,
      [FEATURES.CMS_FULL]: true,
      [FEATURES.ORDER_BULK_ACTIONS]: true,
      [FEATURES.ANALYTICS_BASIC]: true,
      [FEATURES.ANALYTICS_ADVANCED]: true,
      [FEATURES.AUTOMATION_BASIC]: true,
      [FEATURES.AUTOMATION_ADVANCED]: true,
      [FEATURES.CRAFTFLOW_COMMERCE]: true,
      [FEATURES.MULTI_SHOP_SUPPORT]: true,
      [FEATURES.PRIORITY_SUPPORT]: true,
      [FEATURES.TRANSACTIONAL_EMAIL]: true,
      [FEATURES.STRIPE_CHECKOUT]: true,
      [FEATURES.PRIORITY_QUEUE]: true,
      [FEATURES.SCHEDULED_RUNS]: true,
      [FEATURES.WHITE_LABEL]: true,
      [FEATURES.MULTI_USER]: true,
      [FEATURES.ROLE_BASED_ACCESS]: true,
    },
    limits: {
      mockupsPerMonth: 1000,
      mockupsPerBatch: -1,
      templates: -1,
      canvas: -1,
      sizes: -1,
      etsyStores: 5,
      shopifyStores: 5,
      exportsPerMonth: 1000,
      userSeats: 5,
    },
    color: 'amber',
  },
  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    name: 'Enterprise',
    price: null, // Custom pricing
    description: 'Custom solutions for large operations',
    features: {
      [FEATURES.ETSY_INTEGRATION]: true,
      [FEATURES.SHOPIFY_INTEGRATION]: true,
      [FEATURES.BATCH_MOCKUPS]: true,
      [FEATURES.FILE_CLEANER]: true,
      [FEATURES.AUTO_NAMING]: true,
      [FEATURES.BATCH_UPLOADS]: true,
      [FEATURES.ADVANCED_RESIZING]: true,
      [FEATURES.PRINT_FILE_GENERATOR]: true,
      [FEATURES.CSV_EXPORT]: true,
      [FEATURES.AI_TAGGING]: true,
      [FEATURES.AUTO_LAYOUT]: true,
      [FEATURES.ADVANCED_LAYOUT]: true,
      [FEATURES.DPI_SCORING]: true,
      [FEATURES.UTILIZATION_METER]: true,
      [FEATURES.CUSTOM_DOMAIN]: true,
      [FEATURES.CMS_FULL]: true,
      [FEATURES.ORDER_BULK_ACTIONS]: true,
      [FEATURES.ANALYTICS_BASIC]: true,
      [FEATURES.ANALYTICS_ADVANCED]: true,
      [FEATURES.AUTOMATION_BASIC]: true,
      [FEATURES.AUTOMATION_ADVANCED]: true,
      [FEATURES.CRAFTFLOW_COMMERCE]: true,
      [FEATURES.MULTI_SHOP_SUPPORT]: true,
      [FEATURES.PRIORITY_SUPPORT]: true,
      [FEATURES.TRANSACTIONAL_EMAIL]: true,
      [FEATURES.STRIPE_CHECKOUT]: true,
      [FEATURES.PRIORITY_QUEUE]: true,
      [FEATURES.SCHEDULED_RUNS]: true,
      [FEATURES.WHITE_LABEL]: true,
      [FEATURES.MULTI_USER]: true,
      [FEATURES.ROLE_BASED_ACCESS]: true,
    },
    limits: {
      mockupsPerMonth: -1,
      mockupsPerBatch: -1,
      templates: -1,
      canvas: -1,
      sizes: -1,
      etsyStores: -1,
      shopifyStores: -1,
      exportsPerMonth: -1,
      userSeats: -1,
    },
    color: 'emerald',
  },
};

const useSubscriptionStore = create(
  persist(
    (set, get) => ({
      // Current subscription state
      currentTier: SUBSCRIPTION_TIERS.STARTER,
      subscriptionActive: true,
      subscriptionExpiresAt: null,
      subscriptionStatus: 'active',
      // True once GET /subscriptions/current has set subscriptionStatus. That endpoint owns the
      // status the banners read (raw 'past_due'); the entitlements payload only reports its own
      // derived status ('expired' once grace elapses, 'active' for a comp), which must never
      // overwrite it. Not persisted; cleared by resetStore.
      subscriptionStatusFromCurrent: false,

      // Trial state
      trialActive: false,
      trialExpired: false,
      trialEndsAt: null,
      trialStartedAt: null,
      trialDaysRemaining: 0,
      hasActiveAccess: true,

      // Grace period state — set when /current returns is_in_grace_period
      isInGracePeriod: false,
      gracePeriodEndsAt: null,
      // True when the account holds a comp: the server never suspends or deletes it for a
      // failed payment, so the past-due banner must not say it will.
      suspensionExempt: false,

      // Entitlements from backend
      entitlements: {
        features: {},
        limits: {},
        usage: {},
      },

      // Usage tracking
      currentUsage: {
        mockupsThisMonth: 0,
        totalMockups: 0,
        totalMockupImages: 0,
        designsThisMonth: 0,
        mockupsLimit: -1,
        mockupsPerBatchLimit: -1,
        templatesCount: 0,
        templatesLimit: -1,
        canvasCount: 0,
        canvasLimit: -1,
        sizesCount: 0,
        sizesLimit: -1,
      },

      // Loading states
      loading: false,
      error: null,
      initialized: false,
      lastFetchedAt: null, // timestamp of last backend fetch

      // Reset all subscription state — called on logout to prevent cross-account data leaks
      resetStore: () =>
        set({
          currentTier: SUBSCRIPTION_TIERS.STARTER,
          subscriptionActive: true,
          subscriptionExpiresAt: null,
          subscriptionStatus: 'active',
          subscriptionStatusFromCurrent: false,
          trialActive: false,
          trialExpired: false,
          trialEndsAt: null,
          trialStartedAt: null,
          trialDaysRemaining: 0,
          hasActiveAccess: true,
          isInGracePeriod: false,
          gracePeriodEndsAt: null,
          suspensionExempt: false,
          entitlements: { features: {}, limits: {}, usage: {} },
          currentUsage: {
            mockupsThisMonth: 0,
            totalMockups: 0,
            totalMockupImages: 0,
            designsThisMonth: 0,
            mockupsLimit: -1,
            mockupsPerBatchLimit: -1,
            templatesCount: 0,
            templatesLimit: -1,
            canvasCount: 0,
            canvasLimit: -1,
            sizesCount: 0,
            sizesLimit: -1,
          },
          loading: false,
          error: null,
          initialized: false,
          lastFetchedAt: null,
        }),

      // Actions
      setSubscriptionTier: tier =>
        set({
          currentTier: normalizeTier(tier),
          error: null,
        }),

      setSubscriptionStatus: (active, expiresAt = null, status = 'active') =>
        set({
          subscriptionActive: active,
          subscriptionExpiresAt: expiresAt,
          subscriptionStatus: status,
          error: null,
        }),

      setCurrentUsage: usage =>
        set({
          currentUsage: { ...get().currentUsage, ...usage },
        }),

      // Increment mockup usage by the number of images produced.
      // Each design uploaded produces N mockup images — each counts toward the limit.
      incrementMockupUsage: (imageCount = 1) =>
        set(state => ({
          currentUsage: {
            ...state.currentUsage,
            mockupsThisMonth: state.currentUsage.mockupsThisMonth + imageCount,
            totalMockups: state.currentUsage.totalMockups + 1,
            totalMockupImages: state.currentUsage.totalMockupImages + imageCount,
          },
        })),

      // Legacy alias — kept for backward compatibility
      incrementMockupImagesUsage: (count = 1) =>
        set(state => ({
          currentUsage: {
            ...state.currentUsage,
            mockupsThisMonth: state.currentUsage.mockupsThisMonth + count,
            totalMockupImages: state.currentUsage.totalMockupImages + count,
          },
        })),

      setLoading: loading => set({ loading }),
      setError: error => set({ error }),

      // Fetch entitlements from new backend endpoint
      fetchEntitlements: async () => {
        try {
          const token = useAuthStore.getState().userToken;
          const data = await apiCall('/api/v1/subscriptions/entitlements', {}, token).catch(() => null);

          if (data) {
            const tier = normalizeTier(data.tier);
            const isActive = ENTITLED_STATUSES.has(data.subscription_status);
            set({
              entitlements: {
                features: data.features || {},
                limits: data.limits || {},
                usage: data.usage || {},
              },
              currentTier: tier,
              subscriptionActive: isActive,
            });
            // GET /current owns subscriptionStatus (the banners need its raw 'past_due', not the
            // entitlements' derived 'expired'/'active'). Entitlements only fills it in as a
            // fallback while /current has not answered, so the two loads cannot race.
            if (!get().subscriptionStatusFromCurrent) {
              set({ subscriptionStatus: data.subscription_status || 'active' });
            }
          }
        } catch (error) {
          console.error('Error fetching entitlements:', error);
        }
      },

      // Fetch subscription from backend
      fetchSubscription: async () => {
        try {
          set({ loading: true, error: null });

          const token = useAuthStore.getState().userToken;
          const [subscription, usage, entitlements] = await Promise.all([
            apiCall('/api/v1/subscriptions/current', {}, token).catch(() => null),
            apiCall('/api/v1/subscriptions/usage', {}, token).catch(() => null),
            apiCall('/api/v1/subscriptions/entitlements', {}, token).catch(() => null),
          ]);

          if (entitlements) {
            set({
              entitlements: {
                features: entitlements.features || {},
                limits: entitlements.limits || {},
                usage: entitlements.usage || {},
              },
            });
          }

          if (subscription) {
            const isTrialActive = subscription.trial_active || false;
            const isTrialExpired = subscription.trial_expired || false;
            // A seller with no subscriptions row (admin-comp / pre-Stripe) comes back as status 'none' with
            // has_active_access hard-coded false, yet the server deliberately keeps them entitled and
            // GET /entitlements says so. Use the same rule as fetchEntitlements so the two never disagree.
            const hasAccess =
              subscription.status === 'none' ||
              (subscription.has_active_access ?? (subscription.status === 'active' || isTrialActive));
            // Prefer effective_tier (computed by get_effective_tier on the backend)
            // over the raw subscriptions.tier column — it accounts for expired
            // trials, canceled subs within paid period, and grace-period rules.
            const tierForState = subscription.effective_tier || subscription.tier;

            set({
              currentTier: normalizeTier(tierForState),
              subscriptionActive: hasAccess,
              subscriptionStatus: subscription.status || 'active',
              subscriptionStatusFromCurrent: true,
              subscriptionExpiresAt: subscription.current_period_end || null,
              // Trial fields
              trialActive: isTrialActive,
              trialExpired: isTrialExpired,
              trialEndsAt: subscription.trial_ends_at || null,
              trialStartedAt: subscription.trial_started_at || null,
              trialDaysRemaining: subscription.days_remaining || 0,
              hasActiveAccess: hasAccess,
              // Grace period fields — used to show the payment-failed banner
              isInGracePeriod: subscription.is_in_grace_period || false,
              gracePeriodEndsAt: subscription.grace_period_ends_at || null,
              suspensionExempt: subscription.suspension_exempt || false,
            });
          }

          if (usage) {
            set({
              currentUsage: {
                ...get().currentUsage,
                mockupsThisMonth: usage.mockups_created || 0,
                totalMockups: usage.total_mockups || 0,
                totalMockupImages: usage.total_mockup_images || 0,
                designsThisMonth: usage.designs_uploaded || 0,
                mockupsLimit: usage.mockups_limit ?? -1,
                mockupsPerBatchLimit: usage.mockups_per_batch_limit ?? -1,
                templatesCount: usage.templates_count ?? 0,
                templatesLimit: usage.templates_limit ?? -1,
                canvasCount: usage.canvas_count ?? 0,
                canvasLimit: usage.canvas_limit ?? -1,
                sizesCount: usage.sizes_count ?? 0,
                sizesLimit: usage.sizes_limit ?? -1,
              },
            });
          }

          set({ initialized: true, loading: false, lastFetchedAt: Date.now() });
        } catch (error) {
          console.error('Error fetching subscription:', error);
          set({ error: error.message, loading: false, initialized: true });
        }
      },

      // Check if cached subscription data is stale (older than 5 minutes)
      isStale: () => {
        const { lastFetchedAt } = get();
        if (!lastFetchedAt) return true;
        return Date.now() - lastFetchedAt > 5 * 60 * 1000;
      },

      // Client-side trial expiry check — supplements backend enforcement
      isTrialExpiredLocally: () => {
        const { trialActive, trialEndsAt } = get();
        if (!trialActive || !trialEndsAt) return false;
        return new Date(trialEndsAt) <= new Date();
      },

      // Feature access methods - checks entitlements first, falls back to tier config
      hasFeature: featureName => {
        const { currentTier, subscriptionActive, entitlements } = get();

        // If trial has locally expired, block features client-side too
        if (get().isTrialExpiredLocally()) {
          return false;
        }

        // Check entitlements from backend first
        if (entitlements.features && Object.keys(entitlements.features).length > 0) {
          if (featureName in entitlements.features) {
            return subscriptionActive ? entitlements.features[featureName] : false;
          }
        }

        if (!subscriptionActive) {
          // If subscription expired, fall back to starter features
          return TIER_CONFIGS[SUBSCRIPTION_TIERS.STARTER]?.features[featureName] || false;
        }

        const tierConfig = TIER_CONFIGS[currentTier];
        return tierConfig?.features[featureName] || false;
      },

      canUseFeature: (featureName, _requiredAmount = 1) => {
        const { currentTier, subscriptionActive, currentUsage, entitlements } = get();

        if (!subscriptionActive) {
          return false;
        }

        // Check entitlements first
        if (entitlements.features && Object.keys(entitlements.features).length > 0) {
          if (featureName in entitlements.features && !entitlements.features[featureName]) {
            return false;
          }
        }

        const tierConfig = TIER_CONFIGS[currentTier];

        // Check if feature is enabled for tier
        if (!tierConfig?.features[featureName]) {
          // Also check entitlements
          if (!(entitlements.features && entitlements.features[featureName])) {
            return false;
          }
        }

        // Check usage limits
        switch (featureName) {
          case FEATURES.BATCH_MOCKUPS:
            if (tierConfig?.limits?.mockupsPerMonth !== -1) {
              return currentUsage.mockupsThisMonth < (tierConfig?.limits?.mockupsPerMonth || 0);
            }
            return true;

          default:
            return true;
        }
      },

      getFeatureLimit: featureName => {
        const { currentTier, entitlements } = get();
        const tierConfig = TIER_CONFIGS[currentTier];

        // Check entitlements limits first
        if (entitlements.limits && Object.keys(entitlements.limits).length > 0) {
          switch (featureName) {
            case FEATURES.MONTHLY_MOCKUP_LIMIT:
              return entitlements.limits.monthly_mockups ?? tierConfig?.limits?.mockupsPerMonth ?? 0;
            default:
              return null;
          }
        }

        switch (featureName) {
          case FEATURES.MONTHLY_MOCKUP_LIMIT:
            return tierConfig?.limits.mockupsPerMonth || 0;
          default:
            return null;
        }
      },

      getRemainingUsage: featureName => {
        const { currentTier, currentUsage, entitlements } = get();
        const tierConfig = TIER_CONFIGS[currentTier];

        switch (featureName) {
          case FEATURES.BATCH_MOCKUPS: {
            const limit = entitlements.limits?.monthly_mockups ?? tierConfig?.limits?.mockupsPerMonth;
            if (limit === -1) return Infinity;
            return Math.max(0, limit - currentUsage.mockupsThisMonth);
          }
          default:
            return null;
        }
      },

      // Get entitlement usage for a specific limit key
      getEntitlementUsage: limitKey => {
        const { entitlements } = get();
        const limit = entitlements.limits?.[limitKey];
        const used = entitlements.usage?.[limitKey];
        if (limit === undefined || used === undefined) return null;
        return {
          used,
          limit,
          remaining: limit === -1 ? Infinity : Math.max(0, limit - used),
          percentage: limit === -1 ? 0 : limit > 0 ? (used / limit) * 100 : 0,
        };
      },

      getTierConfig: (tier = null) => {
        const targetTier = normalizeTier(tier) || get().currentTier;
        return TIER_CONFIGS[targetTier];
      },

      getAllTierConfigs: () => TIER_CONFIGS,

      getUpgradeRecommendation: feature => {
        const { currentTier } = get();
        const tierOrder = [
          SUBSCRIPTION_TIERS.STARTER,
          SUBSCRIPTION_TIERS.GROWTH,
          SUBSCRIPTION_TIERS.PRO,
          SUBSCRIPTION_TIERS.ENTERPRISE,
        ];
        const currentIndex = tierOrder.indexOf(currentTier);

        // Find the lowest tier that supports this feature that's higher than current
        for (let i = currentIndex + 1; i < tierOrder.length; i++) {
          const tier = tierOrder[i];
          const config = TIER_CONFIGS[tier];
          if (config.features[feature]) {
            return {
              recommendedTier: tier,
              config: config,
            };
          }
        }

        return null;
      },

      // Debug method
      getDebugInfo: () => {
        const state = get();
        return {
          subscription: {
            tier: state.currentTier,
            active: state.subscriptionActive,
            status: state.subscriptionStatus,
            expiresAt: state.subscriptionExpiresAt,
          },
          entitlements: state.entitlements,
          usage: state.currentUsage,
          tierConfig: state.getTierConfig(),
          availableFeatures: Object.keys(FEATURES).filter(feature => state.hasFeature(FEATURES[feature])),
        };
      },
    }),
    {
      name: 'subscription-store',
      partialize: state => ({
        // Only persist usage data, not subscription tier (always fetch fresh from backend)
        currentUsage: state.currentUsage,
        // Don't persist: currentTier, subscriptionActive, subscriptionStatus, subscriptionExpiresAt, initialized
        // This ensures we always fetch fresh subscription data on each session
      }),
    }
  )
);

export default useSubscriptionStore;
